import React from 'react';

// Extract print styles outside the component to prevent repeated injections/re-creations on every render
const PRINT_STYLES = `
  @page {
      size: A4;
      margin: 8mm;
  }
  @media print {
      html, body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          background-color: white !important;
          margin: 0 !important;
          padding: 0 !important;
          min-width: 0 !important;
          min-height: 0 !important;
          overflow: visible !important;
      }
      .ledger-wrapper {
          box-shadow: none !important;
          margin: 0 auto !important;
          width: 210mm !important;
          min-height: 0 !important;
          border: none !important;
      }
      .ledger-box {
          border: 1px solid black !important;
      }
  }
`;

// Helper function to safely format values into INR currency standard
const formatINR = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const strVal = String(value).trim();
  if (strVal === '') return '';

  // Extract numeric part and optional suffix (Dr/Cr)
  const match = strVal.match(/^(-?[\d,.]+)\s*(Dr|Cr)?$/i);
  if (match) {
    const num = parseFloat(match[1].replace(/,/g, ''));
    const suffix = match[2] ? ` ${match[2]}` : '';
    
    // Fallback to original string if not a valid number
    if (isNaN(num)) return strVal;
    
    // Format using INR locale
    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
    
    return `${formatted}${suffix}`;
  }
  
  return strVal; // Return as-is if it doesn't match currency pattern
};

// Helper function to normalize dates to DD-MM-YYYY
const formatDate = (dateString) => {
  if (!dateString) return '';
  const str = String(dateString).trim();

  // If already in DD-MM-YYYY or DD/MM/YYYY format, normalize slashes to dashes and return
  if (/^\d{1,2}[-\/]\d{1,2}[-\/]\d{4}$/.test(str)) {
     const parts = str.split(/[-\/]/);
     return `${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[2]}`;
  }

  // Try parsing as standard date (e.g., YYYY-MM-DD, ISO string)
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  return str; // Fallback to original string if parsing fails
};

const Ledger = ({ data }: { data: any }) => {
  // Safe destructuring with empty object fallback
  const {
    company,
    reportMeta,
    party,
    transactions,
    totals,
    footer
  } = data || {};

  // Safely fallback arrays
  const safeTransactions = transactions || [];
  const safeNotes = footer?.notes || [];

  return (
    <>
      <style>{PRINT_STYLES}</style>

      {/* A4 Size Wrapper */}
      <div className="ledger-wrapper mx-auto bg-white shadow-xl box-border text-[13px] leading-snug flex flex-col font-sans text-black"
        style={{ width: '210mm', padding: '16px 24px', boxSizing: 'border-box' }}
      >
        
        {/* Header Section */}
        <div className="border-b-2 border-black pb-4 mb-4 flex justify-between items-end">
          <div className="w-2/3">
            <h1 className="font-bold text-2xl mb-1 uppercase tracking-wider text-gray-900">
              {company?.name || ''}
            </h1>
            <p className="text-[12px] text-gray-700">{company?.address || ''}</p>
          </div>
          <div className="w-1/3 text-right">
            <h2 className="font-bold text-xl uppercase tracking-widest text-black">Party Ledger</h2>
            <p className="text-[12px] mt-1 font-semibold text-gray-800">
              From: {reportMeta?.fromDate || ''} To: {reportMeta?.toDate || ''}
            </p>
          </div>
        </div>

        {/* Party Details Section */}
        <div className="border border-black p-3 mb-4 flex justify-between bg-gray-50">
          <div className="w-2/3 border-r border-gray-300 pr-4">
            <span className="text-[11px] text-gray-600 uppercase mb-1 block font-medium">Ledger Account</span>
            <div className="font-bold text-[16px] text-gray-900">{party?.name || ''}</div>
            <p className="text-[12px] mt-1 text-gray-800">{party?.address || ''}</p>
          </div>
          <div className="w-1/3 pl-4 flex flex-col justify-center">
            <div className="mb-2">
              <span className="text-[11px] text-gray-600 uppercase font-medium">GSTIN/UIN</span>
              <div className="font-bold text-[14px]">{party?.gstin || 'N/A'}</div>
            </div>
            <div>
              <span className="text-[11px] text-gray-600 uppercase font-medium">Opening Balance</span>
              <div className="font-bold text-[14px]">
                {formatINR(party?.openingBalance?.amount)} <span className="text-xs font-semibold">{party?.openingBalance?.type || ''}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Core Table Section */}
        <div className="ledger-box border border-black flex flex-col flex-grow">
            
          {/* Table Header */}
          <div className="flex border-b border-black font-bold text-center bg-gray-100 text-[12px] uppercase">
            <div className="w-[10%] py-2 border-r border-black">Date</div>
            <div className="w-[33%] py-2 border-r border-black text-left px-3">Particulars</div>
            <div className="w-[12%] py-2 border-r border-black">Vch Type</div>
            <div className="w-[10%] py-2 border-r border-black">Vch No.</div>
            <div className="w-[12%] py-2 border-r border-black text-right px-2">Debit (Dr)</div>
            <div className="w-[12%] py-2 border-r border-black text-right px-2">Credit (Cr)</div>
            <div className="w-[11%] py-2 text-right px-2">Balance</div>
          </div>

          {/* Table Body */}
          <div className="flex-grow flex relative min-h-[400px]">
              
            {/* Absolute Vertical Column Lines */}
            <div className="absolute inset-0 flex pointer-events-none border-b border-black">
              <div className="w-[10%] border-r border-black"></div>
              <div className="w-[33%] border-r border-black"></div>
              <div className="w-[12%] border-r border-black"></div>
              <div className="w-[10%] border-r border-black"></div>
              <div className="w-[12%] border-r border-black"></div>
              <div className="w-[12%] border-r border-black"></div>
              <div className="w-[11%]"></div>
            </div>

            {/* Items Container */}
            <div className="w-full relative z-10 pb-4 text-[12px]">
              {safeTransactions.map((txn, index) => (
                <div key={txn?.id || index} className={`flex w-full ${index === 0 ? 'pt-3' : 'pt-4'} ${txn?.isOpening ? 'text-gray-900' : ''}`}>
                  <div className="w-[10%] px-1 text-center">{formatDate(txn?.date)}</div>
                  <div className="w-[33%] px-3">
                    <span className={txn?.isOpening ? "font-semibold italic" : "font-bold"}>{txn?.particulars?.main || ''}</span>
                    {txn?.particulars?.sub && (
                      <>
                        <br />
                        <span className="text-[11px] text-gray-600 italic">{txn?.particulars?.sub}</span>
                      </>
                    )}
                  </div>
                  <div className={`w-[12%] px-1 text-center ${!txn?.isOpening ? 'text-gray-800' : ''}`}>{txn?.vchType || ''}</div>
                  <div className={`w-[10%] px-1 text-center ${!txn?.isOpening ? 'text-gray-800' : ''}`}>{txn?.vchNo || ''}</div>
                  <div className="w-[12%] px-2 text-right font-medium">{formatINR(txn?.debit)}</div>
                  <div className="w-[12%] px-2 text-right font-medium">{formatINR(txn?.credit)}</div>
                  <div className={`w-[11%] px-2 text-right ${txn?.isOpening ? 'font-bold text-gray-700' : 'font-semibold'}`}>
                    {formatINR(txn?.balance)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals Section */}
          <div className="border-t-2 border-black flex font-bold items-center bg-gray-50 text-[13px]">
            <div className="w-[65%] px-3 py-3 text-right border-r border-black uppercase tracking-wider text-gray-800">Totals</div>
            <div className="w-[12%] px-2 py-3 text-right border-r border-black text-[14px]">{formatINR(totals?.debit)}</div>
            <div className="w-[12%] px-2 py-3 text-right border-r border-black text-[14px]">{formatINR(totals?.credit)}</div>
            <div className="w-[11%] px-2 py-3 text-right text-[14px] text-gray-900">{formatINR(totals?.balance)}</div>
          </div>
            
        </div>

        {/* Footer Section */}
        <div className="flex justify-between items-end mt-6 h-24">
          <div className="w-2/3">
            <p className="text-[11px] text-gray-500 italic">
              {safeNotes.map((note, index) => (
                <React.Fragment key={note?.id || index}>
                  {note}
                  {index < safeNotes.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </div>
          <div className="w-1/3 flex flex-col justify-end items-end relative h-full">
            <div className="text-center w-48">
              <div className="border-t border-gray-400 pt-2 text-[12px] font-semibold uppercase text-gray-700">
                Authorised Signatory
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default Ledger;